import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, CheckCircle, MessageSquare, AlertCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { setDeviceMfaCookie } from '@/utils/deviceMfaCookie';
import { validatePhoneNumber, formatPhoneNumber, getPhoneNumberExample } from '@/utils/phoneValidation';
import { handleTwilioError } from '@/utils/twilioErrorHandler';
import { updateOrganizationPhoneVerification } from '@/utils/organization/updatePhoneVerification';
interface MfaSetupProps {
  onComplete: (phoneNumber?: string) => void;
  onSkip?: () => void;
  userEmail?: string;
  isReset?: boolean;
  isAltEmailEnrollment?: boolean;
  isResellerPortal?: boolean;
}

const MfaSetup: React.FC<MfaSetupProps> = ({
  onComplete,
  onSkip,
  userEmail,
  isReset = false,
  isAltEmailEnrollment = false,
  isResellerPortal = false,
}) => {
  const [step, setStep] = useState<'intro' | 'setup' | 'verify'>('intro');
  const [verificationCode, setVerificationCode] = useState('');
  const factorName = 'SMS Verification';
  const [phoneNumber, setPhoneNumber] = useState('');
  const [orgPhoneNumber, setOrgPhoneNumber] = useState<string>('');
  const [isPhoneReadOnly, setIsPhoneReadOnly] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState<{
    message: string;
    type: 'validation' | 'service' | 'rate_limit' | 'authentication' | 'generic';
    canRetry: boolean;
    suggestAlternative: boolean;
  } | null>(null);
  const { user, enrollMfa } = useAuth();

  const targetEmail = userEmail || user?.email;

  // Fetch organization phone number on component mount (skip for reset flow & alt-email enrollment)
  useEffect(() => {
    if (isReset || isAltEmailEnrollment) return; // Alt-email recipients enter their own phone

    const fetchOrgPhoneNumber = async () => {
      if (!targetEmail) return;

      try {
        const { data, error } = await supabase
          .from('organisations')
          .select('poc_phone')
          .eq('poc_email', targetEmail)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          console.error('[MfaSetup] Error fetching organization phone:', error);
          return;
        }

        if (data?.poc_phone) {
          setOrgPhoneNumber(data.poc_phone);
          setPhoneNumber(data.poc_phone);
          // Reseller portal: prefill as editable default, not read-only
          if (!isResellerPortal) {
            setIsPhoneReadOnly(true);
          }
        }
      } catch (error) {
        console.error('[MfaSetup] Error fetching organization data:', error);
      }
    };

    fetchOrgPhoneNumber();
  }, [targetEmail, isReset, isAltEmailEnrollment, isResellerPortal]);

  const validateAndFormatPhone = (value: string) => {
    const formatted = formatPhoneNumber(value);
    const validation = validatePhoneNumber(formatted);

    if (!validation.isValid && value.length > 0) {
      setPhoneError(validation.error || '');
    } else {
      setPhoneError('');
    }

    setPhoneNumber(formatted);
  };

  const generateMfaSecret = async () => {
    if (!targetEmail) {
      setErrorState({
        message: 'Email is required for MFA setup',
        type: 'validation',
        canRetry: false,
        suggestAlternative: false
      });
      return;
    }

    const validation = validatePhoneNumber(phoneNumber);
    if (!validation.isValid) {
      setPhoneError(validation.error || 'Invalid phone number');
      return;
    }

    try {
      setLoading(true);
      setErrorState(null);

      const requestBody = {
        email: targetEmail,
        factorName: factorName,
        factorType: 'sms',
        phoneNumber: phoneNumber
      };

      const { data, error } = await invokeFunction(supabase, 'enroll-mfa', {
        body: requestBody
      });

      if (error) {
        console.error('[MfaSetup] Error sending verification:', error);
        handleMfaError(error);
        return;
      }

      setStep('setup');
      toast.success('Verification code sent to your phone');
    } catch (error: any) {
      console.error('[MfaSetup] Error generating MFA secret:', error);
      handleMfaError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleMfaError = (error: any) => {
    // Check if it's a structured error response from our edge function
    if (error.errorType === 'twilio_api' && error.twilioError) {
      const twilioErrorResponse = handleTwilioError(error.twilioError);
      setErrorState({
        message: twilioErrorResponse.userMessage,
        type: twilioErrorResponse.errorType,
        canRetry: twilioErrorResponse.canRetry,
        suggestAlternative: false
      });
    } else if (error.errorType) {
      // Structured error from our backend
      setErrorState({
        message: error.error || 'An error occurred',
        type: error.errorType,
        canRetry: error.canRetry !== false,
        suggestAlternative: false
      });
    } else {
      // Generic error
      setErrorState({
        message: 'Failed to generate MFA setup. Please try again.',
        type: 'generic',
        canRetry: true,
        suggestAlternative: false
      });
    }
  };

  const handleVerifyAndEnroll = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    if (!targetEmail) {
      toast.error('Email is required for MFA setup');
      return;
    }

    try {
      setLoading(true);

      const result = await enrollMfa(
        verificationCode,
        'sms',
        targetEmail,
        phoneNumber
      );

      if (result.success) {
        // Store MFA method preference for future verification
        sessionStorage.setItem(`figabl_mfa_method_${targetEmail}`, 'sms');

        // Set device MFA cookie to remember this device
        await setDeviceMfaCookie(targetEmail);

        // Update organization phone verification (skip for alt-email / reseller portal)
        if (!isAltEmailEnrollment && !isResellerPortal) {
          try {
            await updateOrganizationPhoneVerification(targetEmail, phoneNumber);
          } catch (error) {
            console.error('[MfaSetup] Failed to update organization phone verification:', error);
            // Don't block completion on organization update failure
          }
        }

        setStep('verify');
        setTimeout(() => {
          onComplete(phoneNumber);
        }, 2000);
      } else {
        toast.error(result.error || 'Failed to verify code');
      }
    } catch (error: any) {
      console.error('[MfaSetup] Error enrolling MFA:', error);
      toast.error('Failed to complete MFA setup');
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    if (!targetEmail) return;

    try {
      setLoading(true);
      setErrorState(null);

      const requestBody = {
        email: targetEmail,
        factorName: factorName,
        factorType: 'sms',
        phoneNumber: phoneNumber
      };

      const { error } = await invokeFunction(supabase, 'enroll-mfa', {
        body: requestBody
      });

      if (error) {
        handleMfaError(error);
        return;
      }

      toast.success('New verification code sent to your phone');
      setErrorState(null);
    } catch (error: any) {
      console.error('[MfaSetup] Error resending code:', error);
      handleMfaError(error);
    } finally {
      setLoading(false);
    }
  };

  const goBackToPhoneEntry = () => {
    setStep('intro');
    setVerificationCode('');
    setErrorState(null);
    setPhoneError('');
  };

  if (step === 'intro') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <Shield className="w-12 h-12 mx-auto mb-4 text-blue-600" />
          <CardTitle>Secure Your Account</CardTitle>
          <CardDescription>
            Set up SMS two-factor authentication to add an extra layer of security to your account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {errorState && (
            <Alert variant={errorState.type === 'validation' ? 'destructive' : 'warning'}>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {errorState.message}
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="phone-number">Phone Number</Label>
            </div>
            <Input
              id="phone-number"
              placeholder={getPhoneNumberExample()}
              value={phoneNumber}
              onChange={(e) => validateAndFormatPhone(e.target.value)}
              type="tel"
              readOnly={isPhoneReadOnly}
              className={`${phoneError ? 'border-red-500' : ''} ${isPhoneReadOnly ? 'bg-gray-50 cursor-default' : ''}`}
            />
            {isResellerPortal && orgPhoneNumber && !isReset && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  Not your phone? Enter the number where you want to receive the code.
                </AlertDescription>
              </Alert>
            )}
            {isPhoneReadOnly && orgPhoneNumber && !isReset && !isResellerPortal && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  Using phone number from your organization profile. To change it contact an admin.
                </AlertDescription>
              </Alert>
            )}
            {phoneError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{phoneError}</AlertDescription>
              </Alert>
            )}
            {!isPhoneReadOnly && (
              <p className="text-xs text-gray-500">
                Include country code (e.g., {getPhoneNumberExample()})
              </p>
            )}
          </div>

          <Alert>
            <MessageSquare className="h-4 w-4" />
            <AlertDescription>
              We'll send verification codes to your phone number via SMS.
            </AlertDescription>
          </Alert>

          <div className="flex gap-2">
            <Button
              onClick={generateMfaSecret}
              disabled={loading || !targetEmail || (!!phoneError || !phoneNumber)}
              className="flex-1"
            >
              {loading ? 'Setting up...' : 'Set Up SMS Verification'}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (step === 'setup') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <CardTitle>Check Your Phone</CardTitle>
          <CardDescription>
            Enter the 6-digit verification code sent to your phone.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {errorState && (
            <Alert variant={errorState.type === 'validation' ? 'destructive' : 'warning'}>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {errorState.message}
              </AlertDescription>
            </Alert>
          )}

          <div className="text-center space-y-2">
            <p className="text-sm text-gray-600">
              Code sent to: <strong>{phoneNumber}</strong>
            </p>
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={resendCode}
                disabled={loading || errorState?.type === 'rate_limit'}
              >
                {loading ? 'Sending...' : 'Resend Code'}
              </Button>
              {(isResellerPortal || !isPhoneReadOnly) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={goBackToPhoneEntry}
                  disabled={loading}
                >
                  Change Phone Number
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="verification-code">Enter 6-digit code</Label>
            <Input
              id="verification-code"
              placeholder="123456"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="text-center text-lg tracking-widest"
            />
          </div>

          <Button
            onClick={handleVerifyAndEnroll}
            disabled={loading || verificationCode.length !== 6}
            className="w-full"
          >
            {loading ? 'Verifying...' : 'Complete Setup'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-600" />
        <CardTitle>MFA Setup Complete!</CardTitle>
        <CardDescription>
          Your account is now secured with SMS two-factor authentication.
        </CardDescription>
      </CardHeader>
    </Card>
  );
};

export default MfaSetup;
